import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Snackbar, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, IconButton, Chip, Button, InputAdornment,
  Tooltip, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { audioFilesAPI } from '../services/api';
import { resolveUrl } from '../utils/urlUtils';
import { format } from 'date-fns';

export default function AudioFilesManagement() {
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const [audioToDelete, setAudioToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { playTrack } = useAudioPlayer();

  // Fetch audio files
  const fetchAudioFiles = async () => {
    try {
      setLoading(true);
      
      // Use the audioFilesAPI from api.js instead of direct fetch
      const response = await audioFilesAPI.getAllAudioFiles(
        page + 1, 
        rowsPerPage,
        searchTerm,
        filterType,
        filterProvider
      );
      
      setAudioFiles(response.data.data || []);
      setTotalItems(response.data.meta?.total || 0);
    } catch (error) {
      console.error('Error fetching audio files:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load audio files: ' + (error.response?.data?.message || error.message),
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Handle opening delete dialog
  const handleDeleteClick = (audioFile) => {
    setAudioToDelete(audioFile);
    setDeleteDialogOpen(true);
  };

  // Handle closing delete dialog
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setAudioToDelete(null);
  };

  // Handle actual deletion
  const handleDeleteConfirm = async () => {
    if (!audioToDelete?.id) return;
    
    try {
      setDeleteLoading(true);
      await audioFilesAPI.deleteAudioFile(audioToDelete.id);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Audio file deleted successfully',
        severity: 'success'
      });
      
      // Refresh the audio files list
      fetchAudioFiles();
    } catch (error) {
      console.error('Error deleting audio file:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete audio file: ' + (error.response?.data?.message || error.message),
        severity: 'error'
      });
    } finally {
      setDeleteLoading(false);
      handleDeleteDialogClose();
    }
  };

  useEffect(() => {
    fetchAudioFiles();
  }, [page, rowsPerPage, filterType, filterProvider]);

  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchAudioFiles();
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0); // Reset to first page
    fetchAudioFiles();
  };

  // Handle filter changes
  const handleFilterTypeChange = (e) => {
    setFilterType(e.target.value);
    setPage(0); // Reset to first page
  };

  const handleFilterProviderChange = (e) => {
    setFilterProvider(e.target.value);
    setPage(0); // Reset to first page
  };

  // Handle playing audio
  const handlePlayAudio = (audioFile) => {
    const audioUrl = resolveUrl(audioFile.url);
    
    playTrack({
      url: audioUrl,
      title: `${audioFile.bookTitle} (${audioFile.type})`,
      artist: 'Audio File'
    });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (e) {
      return dateString;
    }
  };

  // Get appropriate chip color based on audio type
  const getTypeColor = (type) => {
    switch (type) {
      case 'short':
        return 'success';
      case 'medium':
        return 'primary';
      case 'long':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Get appropriate chip color based on provider
  const getProviderColor = (provider) => {
    switch (provider) {
      case 'openai':
        return 'info';
      case 'google':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Audio Files Management</Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <TextField
            label="Search Audio Files"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ flex: 1, minWidth: '200px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by book title or filename..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              onChange={handleFilterTypeChange}
              label="Type"
              startAdornment={
                <InputAdornment position="start">
                  <FilterListIcon />
                </InputAdornment>
              }
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="short">Short</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="long">Long</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Provider</InputLabel>
            <Select
              value={filterProvider}
              onChange={handleFilterProviderChange}
              label="Provider"
              startAdornment={
                <InputAdornment position="start">
                  <FilterListIcon />
                </InputAdornment>
              }
            >
              <MenuItem value="">All Providers</MenuItem>
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="google">Google</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
          >
            Search
          </Button>
          
          <Tooltip title="Refresh">
            <IconButton 
              onClick={handleRefresh} 
              disabled={loading || refreshing}
              color="primary"
            >
              {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      
      <Paper>
        {loading && !refreshing ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Book</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Filename</TableCell> {/* Added filename column */}
                    <TableCell>Provider</TableCell>
                    <TableCell>Voice</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audioFiles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center"> {/* Updated colspan to 7 */}
                        <Typography variant="body1" sx={{ py: 3 }}>
                          No audio files found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    audioFiles.map((audioFile) => (
                      <TableRow key={audioFile.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {audioFile.bookTitle || 'Unknown Book'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {audioFile.bookId?.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={audioFile.type} 
                            color={getTypeColor(audioFile.type)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {audioFile.fileName || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={audioFile.provider || 'Unknown'} 
                            color={getProviderColor(audioFile.provider)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {audioFile.voiceName || 'Default'}
                        </TableCell>
                        <TableCell>
                          {formatDate(audioFile.createdAt)}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Tooltip title="Play Audio">
                              <IconButton 
                                size="small" 
                                color="primary" 
                                onClick={() => handlePlayAudio(audioFile)}
                              >
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Audio">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeleteClick(audioFile)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>
      
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Audio File Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the audio file 
            {audioToDelete ? ` for "${audioToDelete.bookTitle}" (${audioToDelete.type})` : ''}? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteDialogClose} 
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained" 
            autoFocus
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={16} color="inherit" />}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
